import { createUser, UserData } from '../../utils/types'
import { BASE_URL } from '../endpoints'
import { del, get, getApiParams, patch, post } from '../https'

interface userUpdate {
  role: string
  schoolId?: string
  firstName: string | null
  lastName: string | null
}

export const getSCRData = (
  data: string[],
  page: number,
  sortBy: string,
  sortOrderValue: string,
  activeTab: string
): Promise<unknown> => {
  const schoolsQuery = data?.length > 0
    ? data.map((school) => `schools[]=${encodeURIComponent(school)}`).join('&')
    : ''
  
  const baseParams = `page=${page}&sortBy=${sortBy}&sortOrder=${sortOrderValue}`
  const queryString = schoolsQuery ? `${baseParams}&${schoolsQuery}` : baseParams
  
  const url =
    activeTab === 'tab1'
      ? `${BASE_URL}/single-central-records?${queryString}`
      : `${BASE_URL}/single-central-records/archived?${queryString}&limit=5`

  return get(url, null)
}

export const getAllPolicies = (): Promise<unknown> => {
  return get(`${BASE_URL}/policies/published`)
}
export const getSchoolAllPolicies = (sId: string): Promise<unknown> => {
  return get(`${BASE_URL}/school/${sId}/policies`)
}

export const getSCRDataSingleRecord = (
  data: string,
  id: string
): Promise<unknown> => {
  return get(`${BASE_URL}/single-central-records/${id}?school=${data}`, null)
}

export const getAllTrainigRecordsCategoriesSCR = (): Promise<unknown> => {
  return get(`${BASE_URL}/training-record-categories?sortBy=createdAt&sortOrder=asc`)
}

export const getAllSafeguardingRecordsCategoriesSCR = (): Promise<unknown> => {
  return get(`${BASE_URL}/safeguarding-record-categories?sortBy=createdAt&sortOrder=asc`)
}
export const getAllPolicyRecordsCategoriesSCR = (): Promise<unknown> => {
  return get(`${BASE_URL}/policies/published`)
}
export const getAllRoles = (): Promise<unknown> => {
  return get(`${BASE_URL}/user-roles`)
}
export const getPolicies = (): Promise<unknown> => {
  return get(`${BASE_URL}/policies`)
}

export const createUserApi = (data: createUser): Promise<unknown> => {
  return post(`${BASE_URL}/users`, data, null)
}

export const importUsersApi = (userData: UserData[]): Promise<unknown> => {
  return post(
    `${BASE_URL}/users/import`,
    {
      sendInvitationEmail: true,
      users: userData,
    },
    null
  )
}

export const deleteUser = ({
  id,
}: {
  id: string | undefined
}): Promise<unknown> => {
  return del(`${BASE_URL}/users/${id}`, null)
}

export const getAllSCRPoliciesAgreements = (
  school: string,
  userId: string | undefined
): Promise<unknown> => {
  return get(
    `${BASE_URL}/single-central-records/${userId}/agreements?school=${school}`
  )
}

export const suspendUser = (
  schoolId: string,
  status: string,
  id: string
): Promise<unknown> => {
  return patch(
    `${BASE_URL}/single-central-records/${id}/suspend?school=${schoolId}`,
    {
      status: status,
    },
    null
  )
}

export const archiveUser = (data: string, id: string): Promise<unknown> => {
  return patch(
    `${BASE_URL}/single-central-records/${id}/archived?school=${data}`,

    null
  )
}
export const changeUserRoleApi = (
  data: string,
  id: string
): Promise<unknown> => {
  return patch(
    `${BASE_URL}/users/${id}/change-role`,
    {
      role: data,
    },
    null
  )
}

export const createNote = (
  title: string,
  schoolId: string,
  details: string,
  id: string
): Promise<unknown> => {
  return post(
    `${BASE_URL}/single-central-records/${id}/notes?school=${schoolId}`,
    {
      title: title,
      detail: details,
    },
    null
  )
}

export const updateUser = (data: userUpdate, id: string): Promise<unknown> => {
  return patch(`${BASE_URL}/users/${id}`, data, null)
}

export const getAllNotes = (id: string, school: string): Promise<unknown> => {
  // Construct params object with 'schools' key containing the array of strings
  const params = { school: school }

  return getApiParams(`${BASE_URL}/single-central-records/${id}/notes`, params)
}

export const updateScrNote = (
  title: string,
  detail: string,
  userId: string,
  noteId: string,
  schoolId: string
): Promise<unknown> => {
  return patch(
    `${BASE_URL}/single-central-records/${userId}/notes/${noteId}?school=${schoolId}`,
    {
      title: title,
      detail: detail,
    },
    null
  )
}

export const deleteScrNote = ({
  id,
  noteId,
  schoolId,
}: {
  id: string | undefined
  noteId: string
  schoolId: string
}): Promise<unknown> => {
  return del(
    `${BASE_URL}/single-central-records/${id}/notes/${noteId}?school=${schoolId}`,
    null
  )
}

export const resendVerificationEmail = (id: string): Promise<unknown> => {
  return patch(`${BASE_URL}/users/${id}/resend-verification-email`, null)
}

export const safeguardingRecordComment = (
  id: string,
  safeguardingRecordId: string,
  schoolId: string,
  comment: string
): Promise<unknown> => {
  return post(
    `${BASE_URL}/single-central-records/${id}/safeguarding-records/${safeguardingRecordId}/comments?school=${schoolId}`,
    { comment }
  )
}
export const trainingRecordComment = (
  id: string,
  trainingRecordId: string,
  schoolId: string,
  comment: string
): Promise<unknown> => {
  return post(
    `${BASE_URL}/single-central-records/${id}/training-records/${trainingRecordId}/comments?school=${schoolId}`,
    { comment }
  )
}

export const rejectSafeguardingDocument = (
  id: string,
  status: string
): Promise<unknown> => {
  return patch(`${BASE_URL}/safeguarding-records/${id}/verify`, { status })
}
export const rejectTrainingDocument = (
  id: string,
  status: string
): Promise<unknown> => {
  return patch(`${BASE_URL}/training-records/${id}/verify`, { status })
}
